import React, { useState, useEffect } from 'react';
import {
  Input,
  InputNumber,
  Select,
  Form,
  Button,
  Modal,
  Upload,
  Tooltip,
  Spin,
  Table,
  Typography,
  Tag as AntdTag,
} from 'antd';
import cx from 'classnames';
import { UploadOutlined } from '@ant-design/icons';
import styles from './addnewfilefeed.module.sass';
import mixpanel from 'mixpanel-browser';
import { STATUS } from '../../../../redux/constants';
import tagStyles from '../../../../shared-components/Tag/tag.module.sass';
import GeneralTagMultipleSelect from '../../../../shared-components/GeneralTagMultipleSelect';
import { incrementUIRequestsCounter } from '../../../../redux/utils/websocket/incrementUIRequestsCounter';
import { useDispatch } from 'react-redux';

function CreateFileFeedModal(props) {
  const [form] = Form.useForm();
  const [columnMap, setColumnMap] = useState({});
  const [showMappingStep, setShowMappingStep] = useState(false);
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [enableUploadButton, setEnableUploadButton] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(true);
  const [showFileValidationError, setShowFileValidationError] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [resourceType, setResourceType] = useState('');
  const [editingTags, setEditingTags] = useState(false);
  const [renderTags, setRenderTags] = useState(false);
  const { Text, Paragraph, Title } = Typography;
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      props?.selectedSourceToEdit &&
      Object.keys(props?.selectedSourceToEdit).length > 0
    ) {
      setRenderTags(false);
    } else {
      setRenderTags(true);
    }
  }, []);

  useEffect(() => {
    if (
      props?.selectedSourceToEdit &&
      Object.keys(props?.selectedSourceToEdit).length > 0
    ) {
      form.setFieldsValue(loadInitialValues());
      if (props?.selectedSourceToEdit?.tags) {
        sessionStorage.setItem(
          'tags',
          JSON?.stringify(props?.selectedSourceToEdit?.tags?.map((tag) => tag)),
        );
      } else {
        sessionStorage.setItem('tags', []);
      }
      setRenderTags(true);
    }
  }, [props?.selectedSourceToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAutomappingDetected()) setEnableSaveButton(true);
    setShowSaveButton(true);
  }, [props?.feedTable]);

  const CreateFileSource = (formValues) => {
    const columnMapData = {};
    Object.keys(columnMap).forEach((col) => {
      const mappingColumn = columnMap[col];
      columnMapData[mappingColumn.name] = {
        name: mappingColumn.mapping,
        order: +col,
      };
    });
    let data = {
      state: props.state.toUpperCase(),
      name: formValues?.feed_name,
      threat_type: formValues?.feed_type,
      custom_score: formValues?.feed_custom_score,
      vendor: formValues.feed_vendor,
      feed_description: formValues?.feed_description,
      feed_base64: selectedFile.resource_base64,
      column_mapping: columnMapData,
      resource_type: resourceType,
    };
    if (sessionStorage?.getItem('tags')) {
      data = { ...data, tags: JSON.parse(sessionStorage?.getItem('tags')) };
    }
    props.createFileSource(data);
  };

  const updateSource = (formValues) => {
    let data = {
      uuid: props?.selectedSourceToEdit?.uuid,
      name: formValues.feed_name,
      custom_score: formValues.feed_custom_score,
      vendor: formValues.feed_vendor,
      feed_description: formValues.feed_description,
    };
    if (Object.keys(columnMap).length > 0 || isAutomappingDetected()) {
      const columnMapData = {};
      Object.keys(columnMap).forEach((col) => {
        const mappingColumn = columnMap[col];
        columnMapData[mappingColumn.name] = {
          name: mappingColumn.mapping,
          order: +col,
        };
      });
      data = {
        ...data,
        feed_base64: selectedFile.resource_base64,
        column_mapping: columnMapData,
        resource_type: resourceType,
      };
    }
    data = { ...data, tags: JSON.parse(sessionStorage?.getItem('tags')) };
    props.updateFileSource(data);
  };

  const beforeUpload = (file) => {
    const isValid =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/json' ||
      file.type === 'text/plain';
    return isValid;
  };

  function getBase64(file, callback) {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
      callback({ resource_base64: reader.result.replace(/^.+?;base64,/, '') }),
    );
    reader.readAsDataURL(file);
  }

  const onFileUpload = (fileObject) => {
    setSelectedFile(fileObject);
  };

  const uploadFile = (values) => {
    const fileExtension = values?.fileUpload?.file?.name
      ?.split('.')
      ?.pop()
      ?.toUpperCase();
    setResourceType(fileExtension);
    const uploadObject = {
      state: props.state.toUpperCase(),
      ...selectedFile,
      resource_type: fileExtension,
      threat_type: values?.feed_type,
      //resource_type: values.type,
      //threat_type: values.threatType,
      auth_config: null,
    };
    props.consumeFileData(uploadObject);
    setShowMappingStep(true);
  };

  const handleFileChange = ({ file }) => {
    if (file.originFileObj !== undefined) {
      getBase64(file.originFileObj, onFileUpload);
      setEnableUploadButton(true);
      setShowSaveButton(false);
      setShowFileValidationError(false);
      mixpanel.track('Feed Added File', { feed_type: props?.collection?.type });
    } else {
      setEnableSaveButton(false);
      setEnableUploadButton(false);
      setSelectedFile({});
      setColumnMap({});
      setShowMappingStep(false);
      setShowFileValidationError(true);
    }
    // @TODO: this is triggering twice, not sure why
    if (file.status == 'removed') {
      setEnableSaveButton(false);
      setEnableUploadButton(false);
      setSelectedFile({});
      setColumnMap({});
      setShowMappingStep(false);
      setShowFileValidationError(false);
    }
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const onSave = () => {
    let formValues = form.getFieldsValue();
    form.validateFields().then(() => {
      if (Object.keys(props.selectedSourceToEdit).length === 0) {
        CreateFileSource(formValues);
        props.onCreate();
      } else {
        updateSource(formValues);
        props.onUpdate();
      }
      incrementUIRequestsCounter();
    });
  };

  const handleContainerClick = () => {
    setEditingTags(true);
    onInputChange();
  };

  const handleRemoveTagClick = (tagName) => {
    let updatedTags = JSON.parse(sessionStorage?.getItem('tags'))?.filter(
      (tag) => tag !== tagName,
    );
    sessionStorage.setItem('tags', JSON.stringify(updatedTags));
    onInputChange();
  };

  const handleBlur = () => {
    props.resetTagSearch();
    setEditingTags(false);
  };

  const onInputChange = () => {
    let feed_name = form.getFieldValue('feed_name');
    let feed_type = form.getFieldValue('feed_type');
    if (Object.keys(props?.selectedSourceToEdit).length > 0) {
      if (feed_name) {
        setEnableSaveButton(true);
      } else {
        setEnableSaveButton(false);
      }
    } else {
      if (feed_name && feed_type && Object.keys(columnMap).length > 0) {
        setEnableSaveButton(true);
      } else {
        setEnableSaveButton(false);
      }
    }
  };

  const onUpload = () => {
    let formValues = form.getFieldsValue();
    form.validateFields().then(() => {
      uploadFile(formValues);
      setEnableUploadButton(false);
    });
  };

  const getColumns = () => {
    const { feedTable } = props;
    const rows = feedTable?.rows;
    const firstRow = rows[0];
    const hasHeader = feedTable?.has_header;
    let originalColumns = [];
    if (hasHeader) {
      originalColumns = firstRow;
    }

    const columns = firstRow?.map((text, index, cols) => {
      const col = {
        dataIndex: index,
        key: index,
        render: (text) => (
          <Tooltip title={text?.length >= 27 ? text : ''}>
            <Text>
              {text?.length >= 27 ? text?.substring(0, 24) + '...' : text}
            </Text>
          </Tooltip>
        ),
        title: (filters, sortOrder) => (
          <Select
            placeholder="Select To Map Column"
            defaultActiveFirstOption
            options={props?.feedItemFields}
            onChange={(value) =>
              handleColumnChange(value, index, originalColumns)
            }
          />
        ),
      };
      return col;
    });

    return columns;
  };

  const getData = () => {
    const { feedTable } = props;
    const rows = feedTable.rows.slice(0, 3);

    const data = rows.map((row) => {
      const item = {};
      row.forEach((text, index) => {
        if (typeof text === 'object' && text !== null) {
          item[index] = text?.date;
        } else {
          item[index] = text;
        }
      });
      return item;
    });

    return data;
  };

  const handleColumnChange = (value, index, originalColumns) => {
    const _columnMap = columnMap;
    if (!value) {
      delete _columnMap[index];
    } else {
      const mappedValue = { name: value, mapping: value };
      if (originalColumns && index < originalColumns.length) {
        mappedValue.mapping = originalColumns[index];
      }
      _columnMap[index] = mappedValue;
    }
    setColumnMap(_columnMap);
    setEnableSaveButton(true);
    setShowSaveButton(true);
  };

  const isAutomappingDetected = () => {
    if (
      props?.feedTable?.resource_type === 'STIX' ||
      props?.feedTable?.resource_type === 'THREATCONNECT' ||
      props?.feedTable?.resource_type === 'CROWDSTRIKE'
    )
      return true;
    return false;
  };

  const renderMappingStep = () => {
    let columns;
    let data;
    if (!props.feedTable.rows?.length) return null;
    if (!isAutomappingDetected()) {
      columns = getColumns();
      data = getData();
    }
    return (
      <div>
        {!isAutomappingDetected() ? (
          <>
            <Text>
              Map correct columns in the table with your source (file, URL).
              Only one column per entity (domain, IP, URL and tags) is allowed.
              Make sure to match observables type from file with feed/collection
              type you choose.
            </Text>
            <Table columns={columns} dataSource={data} pagination={false} />
          </>
        ) : props.feedTable?.resource_type === 'STIX' ? (
          <Paragraph>
            STIX is detected and mapping is ready. Click consume button to
            upload the file.
          </Paragraph>
        ) : props.feedTable?.resource_type === 'THREATCONNECT' ? (
          <Paragraph>
            Threatconnect structure detected, mapping ready. Click save to
            upload.
          </Paragraph>
        ) : props.feedTable?.resource_type === 'CROWDSTRIKE' ? (
          <Paragraph>
            CrowdStrike structure detected, mapping ready. Click save to upload.
          </Paragraph>
        ) : (
          ''
        )}
      </div>
    );
  };

  const loadInitialValues = () => {
    let initialValues = {
      feed_name: props?.selectedSourceToEdit?.name,
      feed_type: props?.selectedSourceToEdit?.type,
      feed_vendor: props?.selectedSourceToEdit?.vendor,
      feed_description: props?.selectedSourceToEdit?.feed_description,
      feed_url: props?.selectedSourceToEdit?.url,
      feed_custom_score: props?.selectedSourceToEdit?.custom_score,
    };
    return initialValues;
  };

  const getSaveButtonName = () => {
    if (
      props?.selectedSourceToEdit &&
      Object.keys(props?.selectedSourceToEdit).length === 0
    ) {
      return 'Create';
    }
    return 'Save';
  };

  const getModalTitle = () => {
    if (props?.state.toUpperCase() === 'COLLECTION') {
      if (
        props?.selectedSourceToEdit &&
        Object.keys(props?.selectedSourceToEdit).length > 0
      ) {
        return 'Edit collection';
      }
      return 'Create collection from file';
    } else {
      if (
        props?.selectedSourceToEdit &&
        Object.keys(props?.selectedSourceToEdit).length > 0
      ) {
        return 'Edit feed';
      }
      return 'Create feed from file';
    }
  };

  const getSourceNameFieldName = () => {
    if (props?.state.toUpperCase() === 'COLLECTION') {
      return 'Collection name';
    }
    return 'Feed name';
  };

  const getSourceNameFieldPlaceHolder = () => {
    if (props?.state.toUpperCase() === 'COLLECTION') {
      return 'Enter collection name';
    }
    return 'Enter feed name';
  };

  const getSourceTypeFieldName = () => {
    if (props?.state.toUpperCase() === 'COLLECTION') {
      return 'Collection type';
    }
    return 'Feed type';
  };

  const getSourceTypeFieldPlaceHolder = () => {
    if (props?.state.toUpperCase() === 'COLLECTION') {
      return 'Select collection type';
    }
    return 'Select feed type';
  };

  const renderContent = () => {
    return (
      <Modal
        title={getModalTitle()}
        className="file-feed-modal"
        onCancel={() => props.onCancel()}
        visible={true}
        maskClosable={false}
        width={1000}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              props.onCancel();
            }}
          >
            Cancel
          </Button>,
          <Button
            disabled={!enableSaveButton}
            hidden={!showSaveButton}
            key="submit"
            type="primary"
            onClick={onSave}
          >
            {getSaveButtonName()}
          </Button>,
          <Button
            disabled={!enableUploadButton}
            hidden={showSaveButton}
            type="primary"
            htmlType="submit"
            onClick={() => {
              mixpanel.track('New Source Saved', {
                'feed type': [form.getFieldValue('type')],
                'built with': ['file'],
              });
              onUpload();
            }}
          >
            Upload
          </Button>,
        ]}
      >
        <Form form={form} name="feed" className={styles.add_new_file_feed}>
          <div className={styles.flex_container}>
            <Form.Item
              name="feed_name"
              label={getSourceNameFieldName()}
              rules={[
                {
                  required: true,
                  message: 'Name is a required field.',
                },
              ]}
            >
              <Input
                maxLength={150}
                onChange={() => onInputChange()}
                placeholder={getSourceNameFieldPlaceHolder()}
              />
            </Form.Item>

            <Form.Item
              name="feed_type"
              label={getSourceTypeFieldName()}
              rules={[
                {
                  required: true,
                  message: 'Type is a required field.',
                },
              ]}
            >
              <Select
                allowClear
                placeholder={getSourceTypeFieldPlaceHolder()}
                onChange={() => onInputChange()}
                disabled={
                  props?.selectedSourceToEdit &&
                  Object.keys(props?.selectedSourceToEdit).length > 0
                }
              >
                <Select.Option value="ip">IP</Select.Option>
                <Select.Option value="domain">Domain</Select.Option>
                <Select.Option value="url">URL</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <div className={styles.flex_container}>
            <Form.Item
              name="feed_vendor"
              label={'Vendor'}
              rules={[
                {
                  required: false,
                  message: 'Please input the vendor name!',
                },
              ]}
            >
              <Input
                maxLength={250}
                onChange={() => onInputChange()}
                placeholder="Enter Vendor Name"
              />
            </Form.Item>

            <Form.Item
              name="feed_custom_score"
              label={'Source Score (0-100)'}
              rules={[
                {
                  required: false,
                  message: 'Source score is a required field.',
                },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                onChange={() => onInputChange()}
                placeholder="Enter Source Score"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="feed_description"
            label={'Description'}
            rules={[
              {
                required: false,
                message: 'Please input the description!',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              maxLength={3000}
              onChange={() => onInputChange()}
              placeholder="Enter Description"
            />
          </Form.Item>

          {renderTags ? (
            <div className={styles.tag_list}>
              {editingTags ? (
                <>
                  {sessionStorage?.getItem('tags')
                    ? JSON.parse(sessionStorage?.getItem('tags'))?.map(
                        (tag) => {
                          return (
                            <AntdTag
                              key={`${Math.random()}`}
                              onClose={() => handleRemoveTagClick(tag)}
                              className={cx(styles?.manually_tag)}
                              closable={true}
                            >
                              {tag?.name ? tag?.name : tag}
                            </AntdTag>
                          );
                        },
                      )
                    : ''}
                  <GeneralTagMultipleSelect
                    foundTags={props?.foundTags}
                    searchStatus={props?.searchStatus}
                    searchForTags={props?.searchForTags}
                    onBlur={() => handleBlur()}
                    clearValue
                  />
                </>
              ) : (
                <>
                  {sessionStorage?.getItem('tags')
                    ? JSON.parse(sessionStorage?.getItem('tags'))?.map(
                        (tag) => {
                          return (
                            <AntdTag
                              key={`${Math.random()}`}
                              onClose={() => handleRemoveTagClick(tag)}
                              className={cx(styles?.manually_tag)}
                              closable={true}
                            >
                              {tag?.name ? tag?.name : tag}
                            </AntdTag>
                          );
                        },
                      )
                    : ''}
                  <AntdTag
                    role={'button13'}
                    className={cx(tagStyles?.tag, styles?.add_tag_button)}
                    onClick={() => handleContainerClick()}
                  >
                    + Add Tag
                  </AntdTag>
                </>
              )}
            </div>
          ) : (
            ''
          )}

          <Form.Item
            className="m-0"
            rules={[
              {
                required: false,
                message: 'Please input the file!',
              },
            ]}
            label={
              <>
                Upload File{' '}
                <Text className={styles.file_upload}>
                  (Supported file types are CSV, JSON, TXT, STIX)
                </Text>
              </>
            }
            name="fileUpload"
          >
            <Upload
              className={styles.upload_file}
              maxCount={1}
              customRequest={dummyRequest}
              onChange={handleFileChange}
              beforeUpload={beforeUpload}
              listType="picture"
            >
              <Button role={'button6'} icon={<UploadOutlined />}>
                Select a File
              </Button>
            </Upload>
          </Form.Item>

          <Paragraph className={styles.error}>
            {showFileValidationError ? 'File not supported' : null}
          </Paragraph>

          {showMappingStep ? (
            props?.consumeStatus === STATUS.LOADED ? (
              renderMappingStep()
            ) : (
              <Spin />
            )
          ) : (
            ''
          )}
        </Form>
      </Modal>
    );
  };

  return <>{renderContent()}</>;
}

export default CreateFileFeedModal;
